import React, { useState, useEffect, useContext } from "react";

import { get } from "../../api";
import { AuthContext } from "../../contexts/Auth";
import { SENTINEL_TMA } from "../../constants/sentinel_types";

import Frame from "../../components/Frame";
import FrameHeader from "../../components/FrameHeader";
import LoadingFrame from "../../components/LoadingFrame";
import Button from "../../components/Button";
import SingleColumnPage from "../../components/SingleColumnPage";
import TableItem from "../../components/TableItem";
import Space from "../../components/Space";
import StatusIndicator from "../../components/StatusIndicator";
import AdminPortalTmaReportSummary from "../AdminPortalTmaReportSummary";

import getStatus from "../../helpers/statuses";
import dashboardStyles from "../AssetsFiltered/AssetsFiltered.module.scss";
import reportStyles from "../AdminPortalTmaReport/AdminPortalTmaReport.module.scss";
import PageHeader from "../../components/PageHeader";
import Breadcrumbs from "../../components/Breadcrumbs";
import Breadcrumb from "../../components/Breadcrumb";

function reportPageHeaderTitle() {
  return (
    <Breadcrumbs>
      <b>
        <Breadcrumb title="Route History" />
      </b>
    </Breadcrumbs>
  );
}

const ROUTE_HISTORY_FROM_ID = "route-history-from-date";
const ROUTE_HISTORY_TO_ID = "route-history-to-date";

const DATE_RANGE_OPTIONS = [
  { key: "current_week", label: "Current week" },
  { key: "last_week", label: "Last week" },
  { key: "current_month", label: "Current month" },
  { key: "last_month", label: "Last month" },
];

function getDateRange(key) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();

  const dayOfWeek = (now.getDay() + 6) % 7;

  switch (key) {
    case "current_week": {
      const from = new Date(now);
      from.setDate(now.getDate() - dayOfWeek);
      from.setHours(0, 0, 0, 0);
      const to = new Date(from);
      to.setDate(from.getDate() + 6);
      to.setHours(23, 59, 59, 999);
      return { from, to };
    }
    case "last_week": {
      const from = new Date(now);
      from.setDate(now.getDate() - dayOfWeek - 7);
      from.setHours(0, 0, 0, 0);
      const to = new Date(from);
      to.setDate(from.getDate() + 6);
      to.setHours(23, 59, 59, 999);
      return { from, to };
    }
    case "current_month": {
      const from = new Date(y, m, 1, 0, 0, 0, 0);
      const to = new Date(y, m + 1, 0, 23, 59, 59, 999);
      return { from, to };
    }
    case "last_month": {
      const from = new Date(y, m - 1, 1, 0, 0, 0, 0);
      const to = new Date(y, m, 0, 23, 59, 59, 999);
      return { from, to };
    }
    default:
      return null;
  }
}

function toInputDate(date) {
  return date.toISOString().slice(0, 10);
}

/** Flatten active devices of a given type from /assets results (include_devices). */
function devicesFromAssets(assets, deviceTypeId) {
  const list = [];
  const seenIds = new Set();
  if (!Array.isArray(assets)) return list;
  for (const asset of assets) {
    const raw = asset.devices;
    if (!Array.isArray(raw)) continue;
    const groupIds = (asset.groups || [])
      .map((g) => g.id)
      .filter((id) => id != null);
    for (const d of raw) {
      if (d.devicetype_id !== deviceTypeId) continue;
      if (d.active === false) continue;
      if (seenIds.has(d.id)) continue;
      seenIds.add(d.id);
      list.push({
        ...d,
        _assetName: asset.name,
        _assetId: asset.id,
        _assetGroupIds: groupIds,
      });
    }
  }
  return list;
}

/** Device selected if any of its asset's groups (that you share) is toggled on. */
function selectionForGroups(devices, userGroupIds, includedGroupIds) {
  if (includedGroupIds.size === 0) {
    return new Set();
  }
  return new Set(
    devices
      .filter((d) => {
        const assetOnUser = (d._assetGroupIds || []).filter((id) =>
          userGroupIds.has(id),
        );
        if (assetOnUser.length === 0) {
          return true;
        }
        return assetOnUser.some((id) => includedGroupIds.has(id));
      })
      .map((d) => d.id),
  );
}

const COL_CHECK = { flex: "0 0 48px", minWidth: 48, maxWidth: 48 };

export default function MobileHistory({ deviceTypeId = SENTINEL_TMA }) {
  const { state } = useContext(AuthContext);
  const [dateRangeKey, setDateRangeKey] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesError, setDevicesError] = useState("");

  const [selectedDeviceIds, setSelectedDeviceIds] = useState(new Set());
  const [userGroups, setUserGroups] = useState([]);
  /** When you belong to multiple groups, which group IDs include their devices (default: all). */
  const [includedGroupIds, setIncludedGroupIds] = useState(() => new Set());

  const [showSummary, setShowSummary] = useState(false);

  const multiGroupMode = userGroups.length > 1;

  useEffect(() => {
    let cancelled = false;
    setDevicesLoading(true);
    setDevicesError("");
    setUserGroups([]);

    const assetsReq = get("/assets", {
      active: "true",
      include_devices: "1",
      include_groups: "1",
      orderBy: "name",
      include_last_alarm: "true",
    });

    const userId = state?.user?.user_id;
    const groupsReq =
      userId != null
        ? get(`/users/${userId}/groups`).catch(() => null)
        : Promise.resolve(null);

    Promise.all([assetsReq, groupsReq])
      .then(([assetsRes, groupsRes]) => {
        if (cancelled) return;
        const ug = Array.isArray(groupsRes) ? groupsRes : [];
        setUserGroups(ug);
        const rows = Array.isArray(assetsRes?.results) ? assetsRes.results : [];
        const filtered = devicesFromAssets(rows, deviceTypeId);
        setDevices(filtered);
        const userGroupIds = new Set(ug.map((g) => g.id));
        if (ug.length > 1) {
          const allOn = new Set(ug.map((g) => g.id));
          setIncludedGroupIds(allOn);
          setSelectedDeviceIds(
            selectionForGroups(filtered, userGroupIds, allOn),
          );
        } else {
          setIncludedGroupIds(new Set());
          setSelectedDeviceIds(new Set(filtered.map((d) => d.id)));
        }
      })
      .catch(() => {
        if (cancelled) return;
        setDevicesError("Failed to load assets and devices.");
      })
      .finally(() => {
        if (!cancelled) setDevicesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [deviceTypeId, state?.user?.user_id]);

  function toggleDevice(id) {
    setSelectedDeviceIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleGroupIncluded(groupId) {
    const userGroupIds = new Set(userGroups.map((g) => g.id));
    const next = new Set(includedGroupIds);
    if (next.has(groupId)) next.delete(groupId);
    else next.add(groupId);
    setIncludedGroupIds(next);
    setSelectedDeviceIds(selectionForGroups(devices, userGroupIds, next));
  }

  function handleDatePreset(key) {
    const range = getDateRange(key);
    setDateRangeKey(key);
    setFromDate(toInputDate(range.from));
    setToDate(toInputDate(range.to));
  }

  function handleFromDateChange(val) {
    setFromDate(val);
    setDateRangeKey(null);
  }

  function handleToDateChange(val) {
    setToDate(val);
    setDateRangeKey(null);
  }

  function selectAll() {
    if (multiGroupMode) {
      const allOn = new Set(userGroups.map((g) => g.id));
      const userGroupIds = new Set(userGroups.map((g) => g.id));
      setIncludedGroupIds(allOn);
      setSelectedDeviceIds(selectionForGroups(devices, userGroupIds, allOn));
    } else {
      setSelectedDeviceIds(new Set(devices.map((d) => d.id)));
    }
  }

  function deselectAll() {
    if (multiGroupMode) {
      const userGroupIds = new Set(userGroups.map((g) => g.id));
      const none = new Set();
      setIncludedGroupIds(none);
      setSelectedDeviceIds(selectionForGroups(devices, userGroupIds, none));
    } else {
      setSelectedDeviceIds(new Set());
    }
  }

  const selectedCount = selectedDeviceIds.size;
  const dateRangeValid = fromDate && toDate && fromDate <= toDate;

  const selectedDevices = devices
    .filter((d) => selectedDeviceIds.has(d.id))
    .map(({ _assetName, _assetId, _assetGroupIds, ...d }) => ({
      ...d,
      ...(_assetName ? { report_asset_name: _assetName } : {}),
    }));

  if (showSummary) {
    return (
      <SingleColumnPage headerContent={reportPageHeaderTitle}>
        <AdminPortalTmaReportSummary
          customer={{ name: "My Devices" }}
          devices={selectedDevices}
          fromDate={fromDate}
          toDate={toDate}
          onBack={() => setShowSummary(false)}
        />
      </SingleColumnPage>
    );
  }

  return (
    <SingleColumnPage headerContent={reportPageHeaderTitle}>
      <FrameHeader title="Report" />

      <Frame>
        <div className={reportStyles.topRoutePanel}>
          {!devicesLoading && !devicesError && (
            <div>
              <div className={reportStyles.groupsScopeLabel}>Your groups</div>
              {userGroups.length > 0 ? (
                <div className={reportStyles.groupsScopeList}>
                  {userGroups.map((g) =>
                    multiGroupMode ? (
                      <button
                        key={g.id}
                        type="button"
                        className={`${reportStyles.groupPillToggle} ${
                          includedGroupIds.has(g.id)
                            ? reportStyles.groupPillToggleOn
                            : ""
                        }`}
                        onClick={() => toggleGroupIncluded(g.id)}
                        aria-pressed={includedGroupIds.has(g.id)}
                      >
                        {g.name || `Group #${g.id}`}
                      </button>
                    ) : (
                      <span key={g.id} className={reportStyles.groupPill}>
                        {g.name || `Group #${g.id}`}
                      </span>
                    ),
                  )}
                </div>
              ) : (
                <div
                  className={reportStyles.emptyDevices}
                  style={{ padding: 0 }}
                >
                  None assigned
                </div>
              )}
            </div>
          )}

          <div>
            <div className={reportStyles.routeHistoryDateHeading}>
              Select date range
            </div>
            <div className={reportStyles.routeHistoryDateToolbar}>
              <div className={reportStyles.dateRangeGroup}>
                {DATE_RANGE_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    className={`${reportStyles.dateRangeBtn} ${
                      dateRangeKey === opt.key
                        ? reportStyles.dateRangeBtnActive
                        : ""
                    }`}
                    onClick={() => handleDatePreset(opt.key)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className={reportStyles.routeHistoryDateFields}>
                <div className={reportStyles.routeHistoryFieldPair}>
                  <label
                    className={reportStyles.routeHistoryMiniLabel}
                    htmlFor={ROUTE_HISTORY_FROM_ID}
                  >
                    From
                  </label>
                  <input
                    id={ROUTE_HISTORY_FROM_ID}
                    type="date"
                    className={reportStyles.routeHistoryDateInputCompact}
                    value={fromDate}
                    onChange={(e) => handleFromDateChange(e.target.value)}
                  />
                </div>
                <span className={reportStyles.routeHistoryDateSepCompact}>
                  —
                </span>
                <div className={reportStyles.routeHistoryFieldPair}>
                  <label
                    className={reportStyles.routeHistoryMiniLabel}
                    htmlFor={ROUTE_HISTORY_TO_ID}
                  >
                    To
                  </label>
                  <input
                    id={ROUTE_HISTORY_TO_ID}
                    type="date"
                    className={reportStyles.routeHistoryDateInputCompact}
                    value={toDate}
                    min={fromDate || undefined}
                    onChange={(e) => handleToDateChange(e.target.value)}
                  />
                </div>
              </div>
            </div>
            {fromDate && toDate && fromDate > toDate && (
              <div className={reportStyles.error}>
                End date must be after start date.
              </div>
            )}
          </div>
        </div>
      </Frame>
      <div className={reportStyles.footer}>
        <Button
          theme="primary"
          disabled={selectedCount === 0 || !dateRangeValid}
          onClick={() => setShowSummary(true)}
        >
          {selectedCount === 0
            ? "Select assets to continue"
            : !dateRangeValid
              ? "Select a valid date range"
              : `Generate report for ${selectedCount} assets${
                  selectedCount !== 1 ? "s" : ""
                }`}
        </Button>
      </div>
      <Space size="s" />

      {devicesLoading && (
        <Frame>
          <LoadingFrame />
        </Frame>
      )}

      {devicesError && (
        <Frame>
          <div className={reportStyles.error}>{devicesError}</div>
        </Frame>
      )}

      {!devicesLoading && !devicesError && devices.length === 0 && (
        <Frame>
          <div className={reportStyles.emptyDevices}>
            No active assets with a matching device type were found.
          </div>
        </Frame>
      )}

      {!devicesLoading && !devicesError && devices.length > 0 && (
        <>
          <div className={reportStyles.deviceListHeader}>
            <div className={reportStyles.bulkActions}>
              <button
                type="button"
                className={reportStyles.linkButton}
                onClick={selectAll}
                disabled={selectedCount === devices.length}
              >
                Select all
              </button>
              <span className={reportStyles.separator}>·</span>
              <button
                type="button"
                className={reportStyles.linkButton}
                onClick={deselectAll}
                disabled={selectedCount === 0}
              >
                Deselect all
              </button>
            </div>
          </div>
          <Frame padding={6}>
            <TableItem key="history-cols">
              <div style={COL_CHECK} />
              <Space size="zero">
                <div className={dashboardStyles.header}>Asset</div>
              </Space>
              <Space size="zero">
                <div className={dashboardStyles.header}>Serial</div>
              </Space>
              <Space size="zero">
                <div className={dashboardStyles.header}>Status</div>
              </Space>
            </TableItem>
          </Frame>

          <Frame padding={0}>
            {devices.map((d) => (
              <div
                key={d.id}
                className={reportStyles.clickableDeviceRow}
                onClick={() => toggleDevice(d.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleDevice(d.id);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-pressed={selectedDeviceIds.has(d.id)}
                aria-label={`Include device ${
                  d.serial_number || d.name || d.id
                } in report`}
              >
                <TableItem header={true}>
                  <div
                    style={{
                      ...COL_CHECK,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <input
                      type="checkbox"
                      className={reportStyles.checkbox}
                      checked={selectedDeviceIds.has(d.id)}
                      onChange={() => toggleDevice(d.id)}
                      onClick={(e) => e.stopPropagation()}
                      tabIndex={-1}
                      aria-hidden="true"
                    />
                  </div>
                  <Space size="zero">
                    <span title={d._assetName || ""}>
                      {d._assetName || "—"}
                    </span>
                  </Space>
                  <Space size="zero">
                    {d.serial_number || d.name || `Device #${d.id}`}
                  </Space>
                  <Space size="zero">
                    <div className={dashboardStyles.wrap}>
                      <StatusIndicator status={d.status} padding={0} />
                      <span>&nbsp;{getStatus(d.status).name}</span>
                    </div>
                  </Space>
                </TableItem>
              </div>
            ))}
          </Frame>
        </>
      )}
    </SingleColumnPage>
  );
}
